import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Users, Building2, TrendingUp, Flame, Copy, Plus, Trash2, Download,
  ClipboardList, UserPlus, Shield, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { API } from '../../App';
import { fetchCoachAthletes } from '../../lib/coachAthletes';

const ROLE_LABELS = {
  org_admin: 'Org Admin',
  coach: 'Coach',
  assistant_coach: 'Assistant Coach',
  nutritionist: 'Nutritionist',
  healthcare_professional: 'Healthcare Professional',
  athlete: 'Athlete',
};

const StatCard = ({ label, value, sub, icon: Icon, accent = 'text-amber-400' }) => (
  <Card className="glass border-stone-800 p-4">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className={`w-4 h-4 ${accent}`} />}
      <span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {sub && <div className="text-xs text-stone-500 mt-0.5">{sub}</div>}
  </Card>
);

const CoachDashboard = ({ org, role, onChanged }) => {
  const navigate = useNavigate();

  const needsBilling = org.subscription_status !== 'active' ||
    (org.entitlement_source === 'billing' &&
      (!org.billing_period_end || Date.parse(org.billing_period_end) <= Date.now()));

  if (needsBilling) {
    return <div data-testid="coach-dashboard">
      <h1 className="text-2xl font-bold text-white mb-3">{org.name}</h1>
      <p className="text-amber-300 text-sm">A subscription is required to manage athletes, teams and coaching data.</p>
      {role === 'org_admin' ?
        <SettingsTab org={org} role={role} onChanged={onChanged} /> :
        <p className="text-stone-300 mt-4">Ask your organization administrator to manage billing.</p>}
    </div>;
  }

  return (
    <div data-testid="coach-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-400 mb-1">{ROLE_LABELS[role]} · {org.type?.replace('_', ' ')}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-wider">{org.name}</h1>
        </div>
        <Button onClick={() => navigate(`/teams/plan-builder/${org.id}`)} className="bg-amber-700 hover:bg-amber-600 text-white font-bold" data-testid="new-plan-btn">
          <ClipboardList className="w-4 h-4 mr-2" />
          Workout Plans
        </Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-stone-900 border border-stone-800 flex flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-amber-900/60 data-[state=active]:text-amber-200 text-stone-400">Overview</TabsTrigger>
          <TabsTrigger value="teams" data-testid="tab-teams" className="data-[state=active]:bg-amber-900/60 data-[state=active]:text-amber-200 text-stone-400">Teams</TabsTrigger>
          <TabsTrigger value="athletes" data-testid="tab-athletes" className="data-[state=active]:bg-amber-900/60 data-[state=active]:text-amber-200 text-stone-400">Athletes</TabsTrigger>
          <TabsTrigger value="people" data-testid="tab-people" className="data-[state=active]:bg-amber-900/60 data-[state=active]:text-amber-200 text-stone-400">Invites & Staff</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-amber-900/60 data-[state=active]:text-amber-200 text-stone-400">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><OverviewTab org={org} /></TabsContent>
        <TabsContent value="teams"><TeamsTab org={org} role={role} /></TabsContent>
        <TabsContent value="athletes"><AthletesTab org={org} /></TabsContent>
        <TabsContent value="people"><PeopleTab org={org} role={role} /></TabsContent>
        <TabsContent value="settings"><SettingsTab org={org} role={role} onChanged={onChanged} /></TabsContent>
      </Tabs>
    </div>
  );
};

const OverviewTab = ({ org }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/coach/${org.id}/overview`)
      .then((res) => setData(res.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load overview.'))
      .finally(() => setLoading(false));
  }, [org.id]);

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-7 h-7 text-amber-400 animate-spin" /></div>;
  if (!data) return null;

  const wt = data.weight_trend || {};
  return (
    <div className="mt-4" data-testid="overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Athletes" value={data.total_athletes} icon={Users} />
        <StatCard label="Teams" value={data.total_teams} icon={Building2} />
        <StatCard label="Workout Compliance" value={`${data.workout_compliance_pct}%`} sub="last 7 days" icon={CheckCircle2} accent="text-emerald-400" />
        <StatCard label="Nutrition Compliance" value={`${data.nutrition_compliance_pct}%`} sub="last 7 days" icon={Flame} accent="text-emerald-400" />
        <StatCard label="Active Today" value={data.active_today} icon={TrendingUp} accent="text-emerald-400" />
        <StatCard label="Inactive 3+ Days" value={data.inactive_3plus_days} icon={AlertTriangle} accent="text-blood-400" />
        <StatCard label="Avg Protein" value={`${data.avg_protein}g`} sub="per logged day" icon={Flame} />
        <StatCard label="Avg Calories" value={data.avg_calories} sub="per logged day" icon={Flame} />
      </div>
      <Card className="glass border-stone-800 p-4 mt-3">
        <span className="text-[10px] uppercase tracking-widest text-stone-500">Weight Trend (30 days)</span>
        <div className="flex flex-wrap gap-6 mt-2 text-sm">
          <span className="text-emerald-400 font-bold">{wt.losing || 0} <span className="text-stone-400 font-normal">losing</span></span>
          <span className="text-stone-200 font-bold">{wt.stable || 0} <span className="text-stone-400 font-normal">stable</span></span>
          <span className="text-amber-400 font-bold">{wt.gaining || 0} <span className="text-stone-400 font-normal">gaining</span></span>
          <span className="text-stone-400">Avg change: <span className="text-white font-bold">{wt.avg_change_30d > 0 ? '+' : ''}{wt.avg_change_30d}</span></span>
        </div>
      </Card>
    </div>
  );
};

const TeamsTab = ({ org, role }) => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [sport, setSport] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/orgs/${org.id}/teams`)
      .then((res) => setTeams(res.data.teams || []))
      .catch(() => toast.error('Failed to load teams.'))
      .finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/orgs/${org.id}/teams`, { name: name.trim(), sport_or_group: sport.trim() || null });
      toast.success('Team created.');
      setName(''); setSport('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mt-4" data-testid="teams-tab">
      {['org_admin', 'coach'].includes(role) && (
        <Card className="glass border-stone-800 p-4 mb-4">
          <form onSubmit={handleCreate} className="flex flex-wrap gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Team name (e.g. Varsity Football)" maxLength={80} className="bg-stone-900 border-stone-700 text-white flex-1 min-w-[180px]" data-testid="team-name-input" />
            <Input value={sport} onChange={(e) => setSport(e.target.value)} placeholder="Sport / group (optional)" maxLength={80} className="bg-stone-900 border-stone-700 text-white flex-1 min-w-[140px]" data-testid="team-sport-input" />
            <Button type="submit" disabled={creating} className="bg-amber-700 hover:bg-amber-600 text-white font-bold" data-testid="team-create-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Create Team</>}
            </Button>
          </form>
        </Card>
      )}
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
      ) : teams.length === 0 ? (
        <Card className="glass border-stone-800 p-6 text-center"><p className="text-stone-400 text-sm">No teams yet. Create your first team above.</p></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {teams.map((t) => (
            <Card key={t.id} className="glass border-stone-800 p-4 hover:border-amber-800 transition-colors cursor-pointer" onClick={() => navigate(`/teams/team/${org.id}/${t.id}`)} data-testid={`team-card-${t.id}`}>
              <h3 className="text-white font-bold tracking-wide">{t.name}</h3>
              <p className="text-xs text-stone-500 mt-1">{t.sport_or_group || 'General'} · {t.athlete_count} athlete{t.athlete_count === 1 ? '' : 's'}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const AthletesTab = ({ org }) => {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState('all');
  const [activityFilter, setActivityFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/orgs/${org.id}/teams`).then((res) => setTeams(res.data.teams || [])).catch(() => {});
  }, [org.id]);

  useEffect(() => {
    setLoading(true);
    setAthletes([]);
    const controller = new AbortController();
    fetchCoachAthletes(axios, API, org.id, teamFilter !== 'all' ? teamFilter : null, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setAthletes(rows); })
      .catch((e) => { if (!controller.signal.aborted) toast.error(e.response?.data?.detail || 'Failed to load athletes.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [org.id, teamFilter]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = teamFilter !== 'all' ? `?team_id=${teamFilter}` : '';
      const res = await axios.get(`${API}/coach/${org.id}/export.csv${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `athletes_report.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded.');
    } catch (e) {
      toast.error('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const filtered = athletes.filter((a) => {
    if (activityFilter === 'active_today') return a.active_today;
    if (activityFilter === 'inactive') return a.inactive_3plus_days;
    if (activityFilter === 'low_compliance') return a.workout_compliance_pct < 40;
    return true;
  });

  return (
    <div className="mt-4" data-testid="athletes-tab">
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger className="bg-stone-900 border-stone-700 text-white w-44" data-testid="athlete-team-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-stone-900 border-stone-700 text-white">
            <SelectItem value="all" className="focus:bg-stone-800 focus:text-white">All Teams</SelectItem>
            {teams.map((t) => <SelectItem key={t.id} value={t.id} className="focus:bg-stone-800 focus:text-white">{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={activityFilter} onValueChange={setActivityFilter}>
          <SelectTrigger className="bg-stone-900 border-stone-700 text-white w-44" data-testid="athlete-activity-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-stone-900 border-stone-700 text-white">
            <SelectItem value="all" className="focus:bg-stone-800 focus:text-white">All Athletes</SelectItem>
            <SelectItem value="active_today" className="focus:bg-stone-800 focus:text-white">Active Today</SelectItem>
            <SelectItem value="inactive" className="focus:bg-stone-800 focus:text-white">Inactive 3+ Days</SelectItem>
            <SelectItem value="low_compliance" className="focus:bg-stone-800 focus:text-white">Low Compliance</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleExport} disabled={exporting} variant="outline" className="ml-auto border-stone-700 bg-stone-900 text-stone-200 hover:bg-stone-800" data-testid="export-csv-btn">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" />Export CSV</>}
        </Button>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="glass border-stone-800 p-6 text-center"><p className="text-stone-400 text-sm">No athletes found. Invite athletes from the "Invites & Staff" tab.</p></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <Card key={a.user_id} className="glass border-stone-800 p-4 hover:border-amber-800 transition-colors cursor-pointer" onClick={() => navigate(`/teams/athlete/${org.id}/${a.user_id}`)} data-testid={`athlete-row-${a.user_id}`}>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="min-w-[140px]">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${a.active_today ? 'bg-emerald-400' : a.inactive_3plus_days ? 'bg-blood-500' : 'bg-stone-500'}`} />
                    <span className="text-white font-bold">{a.username}</span>
                  </div>
                  <p className="text-[11px] text-stone-500">{(a.teams || []).map((t) => t.name).join(', ') || 'No team'}</p>
                </div>
                <div className="text-xs text-stone-400">Workouts <span className="text-white font-bold">{a.workout_days_7d}/7</span> <span className="text-stone-500">({a.workout_compliance_pct}%)</span></div>
                <div className="text-xs text-stone-400">Nutrition <span className="text-white font-bold">{a.nutrition_days_7d}/7</span> <span className="text-stone-500">({a.nutrition_compliance_pct}%)</span></div>
                <div className="text-xs text-stone-400">Avg <span className="text-white font-bold">{a.avg_calories_7d}</span> cal · <span className="text-white font-bold">{a.avg_protein_7d}g</span> protein</div>
                <div className="text-xs text-stone-500 ml-auto">Last workout: {a.last_workout_date || 'never'}</div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const PeopleTab = ({ org, role }) => {
  const [invites, setInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteRole, setInviteRole] = useState('athlete');
  const [inviteTeam, setInviteTeam] = useState('none');
  const [inviteEmail, setInviteEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [newInvite, setNewInvite] = useState(null);

  const load = useCallback(async () => {
    try {
      const [invRes, memRes, teamRes] = await Promise.all([
        axios.get(`${API}/orgs/${org.id}/invites`),
        axios.get(`${API}/orgs/${org.id}/members`),
        axios.get(`${API}/orgs/${org.id}/teams`),
      ]);
      setInvites(invRes.data.invites || []);
      setMembers(memRes.data.members || []);
      setTeams(teamRes.data.teams || []);
    } catch (e) {
      toast.error('Failed to load members.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await axios.post(`${API}/orgs/${org.id}/invites`, {
        role: inviteRole,
        team_id: inviteTeam !== 'none' ? inviteTeam : null,
        email: inviteEmail.trim() || null,
      });
      setNewInvite(res.data);
      setInviteEmail('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite.');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${code}`);
    toast.success('Invite link copied.');
  };

  const revokeInvite = async (id) => {
    try {
      await axios.delete(`${API}/orgs/${org.id}/invites/${id}`);
      setInvites((inv) => inv.filter((i) => i.id !== id));
      toast.success('Invite revoked.');
    } catch (e) { toast.error('Failed to revoke.'); }
  };

  const changeRole = async (uid, newRole) => {
    try {
      await axios.put(`${API}/orgs/${org.id}/members/${uid}/role`, { role: newRole });
      toast.success('Role updated.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update role.'); }
  };

  const removeMember = async (uid) => {
    try {
      await axios.delete(`${API}/orgs/${org.id}/members/${uid}`);
      toast.success('Member removed.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove member.'); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>;

  const canInvite = ['org_admin', 'coach'].includes(role);
  const isAdmin = role === 'org_admin';

  return (
    <div className="mt-4 space-y-6" data-testid="people-tab">
      {canInvite && (
        <Card className="glass border-stone-800 p-4">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-bold text-white tracking-wider uppercase">Invite People</h3>
          </div>
          <form onSubmit={handleInvite} className="flex flex-wrap gap-2">
            <Select value={inviteRole} onValueChange={setInviteRole}>
              <SelectTrigger className="bg-stone-900 border-stone-700 text-white w-40" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-stone-900 border-stone-700 text-white">
                <SelectItem value="athlete" className="focus:bg-stone-800 focus:text-white">Athlete</SelectItem>
                {isAdmin && (
                  <>
                    <SelectItem value="coach" className="focus:bg-stone-800 focus:text-white">Coach</SelectItem>
                    <SelectItem value="assistant_coach" className="focus:bg-stone-800 focus:text-white">Assistant Coach</SelectItem>
                    <SelectItem value="nutritionist" className="focus:bg-stone-800 focus:text-white">Nutritionist</SelectItem>
                    <SelectItem value="healthcare_professional" className="focus:bg-stone-800 focus:text-white">Healthcare Professional</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            <Select value={inviteTeam} onValueChange={setInviteTeam}>
              <SelectTrigger className="bg-stone-900 border-stone-700 text-white w-44" data-testid="invite-team-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-stone-900 border-stone-700 text-white">
                <SelectItem value="none" className="focus:bg-stone-800 focus:text-white">No team (org only)</SelectItem>
                {teams.map((t) => <SelectItem key={t.id} value={t.id} className="focus:bg-stone-800 focus:text-white">{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} type="email" placeholder="Email (optional — locks invite)" className="bg-stone-900 border-stone-700 text-white flex-1 min-w-[180px]" data-testid="invite-email-input" />
            <Button type="submit" disabled={creating} className="bg-amber-700 hover:bg-amber-600 text-white font-bold" data-testid="invite-create-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Invite'}
            </Button>
          </form>
          {newInvite && (
            <div className="mt-4 bg-amber-950/40 border border-amber-800 rounded-lg p-4" data-testid="new-invite-result">
              <p className="text-xs text-stone-400 mb-1">Share this code or link — free for athletes:</p>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-2xl font-bold text-amber-400 tracking-[0.3em]">{newInvite.invite_code}</span>
                <Button size="sm" onClick={() => copyLink(newInvite.invite_code)} className="bg-stone-800 hover:bg-stone-700 text-stone-200" data-testid="copy-invite-link">
                  <Copy className="w-3.5 h-3.5 mr-1" />Copy Link
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Pending invites */}
      <div>
        <h3 className="text-sm font-bold text-white tracking-wider uppercase mb-2">Invites</h3>
        {invites.length === 0 ? (
          <p className="text-stone-500 text-sm">No invites yet.</p>
        ) : (
          <div className="space-y-1.5">
            {invites.map((inv) => (
              <Card key={inv.id} className="glass border-stone-800 px-4 py-2.5 flex flex-wrap items-center gap-3" data-testid={`invite-row-${inv.id}`}>
                <span className="font-mono font-bold text-amber-400 tracking-widest">{inv.invite_code}</span>
                <span className="text-xs text-stone-400">{ROLE_LABELS[inv.role] || inv.role}{inv.email ? ` · ${inv.email}` : ''}</span>
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${inv.status === 'pending' ? 'text-emerald-400 border-emerald-800' : 'text-stone-500 border-stone-700'}`}>{inv.status}</span>
                {inv.uses > 0 && <span className="text-[10px] text-stone-500">{inv.uses} joined</span>}
                <div className="ml-auto flex gap-1">
                  <button type="button" onClick={() => copyLink(inv.invite_code)} className="text-stone-500 hover:text-amber-400 p-1" aria-label="Copy link"><Copy className="w-4 h-4" /></button>
                  {canInvite && <button type="button" onClick={() => revokeInvite(inv.id)} className="text-stone-500 hover:text-blood-500 p-1" aria-label="Revoke" data-testid={`revoke-invite-${inv.id}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Members */}
      <div>
        <h3 className="text-sm font-bold text-white tracking-wider uppercase mb-2 flex items-center gap-2"><Shield className="w-4 h-4 text-amber-400" />Members ({members.length})</h3>
        <div className="space-y-1.5">
          {members.map((m) => (
            <Card key={m.user_id} className="glass border-stone-800 px-4 py-2.5 flex flex-wrap items-center gap-3" data-testid={`member-row-${m.user_id}`}>
              <div className="min-w-[140px]">
                <span className="text-white font-medium">{m.username}</span>
                <p className="text-[11px] text-stone-500">{m.email}</p>
              </div>
              {isAdmin ? (
                <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                  <SelectTrigger className="bg-stone-900 border-stone-700 text-stone-300 w-40 h-8 text-xs" data-testid={`member-role-${m.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-white">
                    {Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v} className="focus:bg-stone-800 focus:text-white">{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <span className="text-xs text-amber-400 uppercase tracking-wider">{ROLE_LABELS[m.role]}</span>
              )}
              {isAdmin && (
                <button type="button" onClick={() => removeMember(m.user_id)} className="ml-auto text-stone-500 hover:text-blood-500 p-1" aria-label="Remove member" data-testid={`remove-member-${m.user_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const SettingsTab = ({ org, role, onChanged }) => {
  const [name, setName] = useState(org.name);
  const [type, setType] = useState(org.type);
  const [saving, setSaving] = useState(false);

  const status = org.subscription_status;
  const [billing, setBilling] = useState(null);
  const [billingConfig, setBillingConfig] = useState(null);
  const [billingPlan, setBillingPlan] = useState(org.requested_plan_tier || org.plan_tier || 'coach');
  const [billingBusy, setBillingBusy] = useState(false);
  const isAdmin = role === 'org_admin';

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(
        `${API}/orgs/${org.id}`,
        {
          name,
          type,
        }
      );

      toast.success(
        'Organization updated.'
      );

      onChanged && onChanged();
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || 'Failed to update.'
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/subscriptions/status`, { params: { owner_type: 'organization', organization_id: org.id } })
        .then((r) => setBilling(r.data)).catch(() => {});
      axios.get(`${API}/subscriptions/config`).then((r) => setBillingConfig(r.data)).catch(() => {});
    }
  }, [org.id, isAdmin]);

  const beginBilling = async () => {
    try {
      setBillingBusy(true);
      const r = await axios.post(`${API}/subscriptions/checkout`, {
        owner_type: 'organization', organization_id: org.id, plan_key: billingPlan
      });
      if (r.data.url) window.location.assign(r.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to start team billing.');
      setBillingBusy(false);
    }
  };
  const manageBilling = async () => {
    try {
      setBillingBusy(true);
      const r = await axios.post(`${API}/subscriptions/portal`, {
        owner_type: 'organization', organization_id: org.id
      });
      if (r.data.url) window.location.assign(r.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to open billing portal.');
      setBillingBusy(false);
    }
  };

  return (
    <div
      className="mt-4 space-y-4 max-w-xl"
      data-testid="settings-tab"
    >
      <Card className="glass border-stone-800 p-5">
        <h3 className="text-sm font-bold text-white tracking-wider uppercase mb-3">Subscription</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-stone-300" data-testid="subscription-status">{status}</span>
          <span className="text-xs text-stone-400">Plan: {org.entitlement_source === 'pending_billing' ? (org.requested_plan_tier || 'coach') + ' (awaiting payment)' : org.plan_tier}. {org.entitlement_source === 'billing' ? 'Stripe subscription' : org.entitlement_source === 'pending_billing' ? 'Payment required' : 'Beta access'}</span>
        </div>
        <p className="mt-2 text-xs text-stone-500">Subscription access is verified through platform administration and billing. Organization admins can manage recurring plans here.</p>
        {isAdmin && ['active', 'trialing', 'past_due', 'unpaid', 'incomplete'].includes(billing?.status) && (
          <div className="mt-3 text-stone-300 text-sm">
            {billing.status === 'past_due' || billing.status === 'unpaid' || billing.status === 'incomplete' ?
              'Payment needs attention in your Stripe billing portal.' :
              billing.cancel_at_period_end ? 'Cancellation scheduled.' : 'Auto-renewal enabled.'}
            <Button disabled={billingBusy} onClick={manageBilling} className="mt-2 block bg-stone-800 text-white">Manage Billing</Button>
          </div>
        )}
        {isAdmin && !['active', 'trialing', 'past_due', 'unpaid', 'incomplete'].includes(billing?.status) && (
          <div className="mt-3 space-y-3">
            <p className="text-xs text-stone-400">Choose a recurring organization subscription. Stripe shows the price and billing interval before payment.</p>
            <select value={billingPlan} onChange={(e) => setBillingPlan(e.target.value)} className="bg-stone-900 text-white border border-stone-700 rounded p-2" data-testid="organization-billing-plan">
              {['coach','team','school'].map((p) => <option key={p} value={p}>{p.toUpperCase()} — ${p === 'coach' ? 39 : p === 'team' ? 99 : 199}/month</option>)}
            </select>
            <Button onClick={beginBilling} disabled={billingBusy || !billingConfig?.plans?.[`organization:${billingPlan}`]} className="ml-3 bg-amber-700 text-white" data-testid="organization-subscribe-btn">Subscribe</Button>
            {!billingConfig?.plans?.[`organization:${billingPlan}`] && <p className="text-xs text-amber-300">This subscription plan is not configured yet.</p>}
          </div>
        )}
      </Card>

      {isAdmin && (
        <Card className="glass border-stone-800 p-5">
          <h3 className="text-sm font-bold text-white tracking-wider uppercase mb-3">
            Organization Details
          </h3>

          <form
            onSubmit={handleSave}
            className="space-y-3"
          >
            <div>
              <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">
                Name
              </label>

              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                className="bg-stone-900 border-stone-700 text-white"
                data-testid="settings-org-name"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">
                Type
              </label>

              <Select
                value={type}
                onValueChange={setType}
              >
                <SelectTrigger
                  className="bg-stone-900 border-stone-700 text-white"
                  data-testid="settings-org-type"
                >
                  <SelectValue />
                </SelectTrigger>

                <SelectContent className="bg-stone-900 border-stone-700 text-white">
                  {[
                    'school',
                    'gym',
                    'personal_trainer',
                    'sports_team',
                    'other',
                  ].map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      className="focus:bg-stone-800 focus:text-white"
                    >
                      {t.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="bg-amber-700 hover:bg-amber-600 text-white font-bold"
              data-testid="settings-save-btn"
            >
              {saving
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : 'Save Changes'}
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
};

export default CoachDashboard;
